import { useCallback, useRef } from 'react';
import type ReactQuill from 'react-quill';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { translateKey } from 'src/locales/translate';
import { useDictation } from '../hooks/useDictation';
import {
  applyInterim,
  applyFinal,
  InsertionState,
  QuillOps,
} from '../utils/dictationInsert';

interface DictationMicButtonProps {
  quillRef: React.RefObject<ReactQuill>;
  disabled?: boolean;
}

export default function DictationMicButton({
  quillRef,
  disabled,
}: DictationMicButtonProps) {
  // Gate on the *currently selected* role (isProfessionalRoleSelected), NOT on
  // role membership (isProfessionalRole) — a user who has the professional role
  // but is acting as an administrator must not see the mic. This mirrors how the
  // note components gate their template selectors.
  const { isProfessionalRoleSelected } = useOrganizationContext();
  const stateRef = useRef<InsertionState>({ anchor: 0, interimLen: 0 });

  const getOps = useCallback((): QuillOps | null => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return null;
    return {
      deleteText: (i, l) => quill.deleteText(i, l, 'api'),
      insertText: (i, t) => quill.insertText(i, t, 'api'),
      setSelection: (i) => quill.setSelection(i, 0, 'api'),
    };
  }, [quillRef]);

  const handleInterim = useCallback(
    (text: string) => {
      const ops = getOps();
      if (ops) stateRef.current = applyInterim(ops, stateRef.current, text);
    },
    [getOps],
  );
  const handleFinal = useCallback(
    (text: string) => {
      const ops = getOps();
      if (ops) stateRef.current = applyFinal(ops, stateRef.current, text);
    },
    [getOps],
  );

  const { isRecording, isConnecting, start, stop } = useDictation({
    onInterim: handleInterim,
    onFinal: handleFinal,
  });

  const handleToggle = useCallback((): void => {
    if (disabled) return;
    if (isRecording || isConnecting) {
      stop();
      return;
    }
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const selection = quill.getSelection(true);
    stateRef.current = {
      anchor: selection ? selection.index : quill.getLength(),
      interimLen: 0,
    };
    void start();
  }, [disabled, isConnecting, isRecording, quillRef, start, stop]);

  if (!isProfessionalRoleSelected) return null;

  const label = isConnecting
    ? translateKey('dictation.connecting')
    : isRecording
    ? translateKey('dictation.stop')
    : translateKey('dictation.start');

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          color={isRecording ? 'error' : 'default'}
          disabled={disabled}
          onClick={handleToggle}
          aria-label={label}
        >
          {isConnecting ? (
            <CircularProgress size={18} />
          ) : isRecording ? (
            <StopCircleRoundedIcon fontSize="small" />
          ) : (
            <MicNoneRoundedIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
